(function (scope) {
  'use strict';
  const config = scope.KickisBackorderConfig || {variants: {}, text: {}};
  const text = config.text;
  const approved = {...config.variants};
  for (const product of config.products || []) {
    for (const id of product.variantIds || []) approved[String(id)] = product.handle;
  }
  const pilot = id => Object.hasOwn(approved, String(id));
  const managed = item => pilot(item.variant_id || item.id) || item.properties?._KickisBestallningsvara === '1';
  let cartItems = [];
  function eligible(variant) {
    return Boolean(variant && pilot(variant.id) && variant.available && variant.inventory_policy === 'continue' && variant.inventory_management === 'shopify');
  }
  function isBackorder(variant, quantity) {
    return eligible(variant) && Number.isFinite(variant.inventory_quantity) && quantity > Math.max(0, variant.inventory_quantity);
  }
  function properties(current, delayed) {
    const result = {...current};
    if (delayed) {
      result.Leverans = text.message;
      result.Samleverans = text.combined;
      result._KickisBestallningsvara = '1';
    } else if (result._KickisBestallningsvara === '1') {
      delete result.Leverans;
      delete result.Samleverans;
      delete result._KickisBestallningsvara;
    }
    return result;
  }
  const root = () => scope.Shopify?.routes?.root || '/';
  async function json(url, body) {
    if (scope.KickisKitHttp) return scope.KickisKitHttp.json(url, body);
    const response = await fetch(url, {credentials:'same-origin',cache:'no-store',signal:AbortSignal.timeout(15000),...(body ? {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)} : {})});
    const data = await response.json();
    if (!response.ok) throw Error(data.description || text.error);
    return data;
  }
  async function stock(items) {
    const result = new Map();
    const handles = new Set(items.filter(managed).map(item => approved[String(item.variant_id || item.id)] || item.handle).filter(Boolean));
    for (const handle of handles) {
      const product = await json(`${root()}products/${encodeURIComponent(handle)}?view=pattern-stock&_backorder=${Date.now()}`);
      if (!Array.isArray(product.variants)) throw Error(text.error);
      for (const variant of product.variants) result.set(String(variant.id), variant);
    }
    return result;
  }
  function totalFor(items, id) {
    return items.filter(item => String(item.variant_id || item.id) === String(id)).reduce((sum, item) => sum + Number(item.quantity), 0);
  }
  const requestedQuantity = (id, quantity) => Number(quantity) + totalFor(cartItems, id);
  function assertOrderable(variant, quantity) {
    if (!variant?.available || !Number.isFinite(variant.inventory_quantity) || !Number.isInteger(quantity) || quantity < 1) throw Error(text.unavailable);
    if (!eligible(variant) && quantity > Math.max(0, variant.inventory_quantity)) throw Error(text.unavailable);
  }
  async function prepareItems(items) {
    if (scope.KickisKitStock && items.some(scope.KickisKitStock.isKit)) {
      await scope.KickisKitStock.prepareItems(items, await json(root()+'cart.js'));
    }
    if (!items.some(managed)) return items;
    if (items.some(item=>managed(item)&&(!Number.isInteger(Number(item.quantity))||Number(item.quantity)<1))) throw Error(text.unavailable);
    const [variants, cart] = await Promise.all([stock(items), json(`${root()}cart.js`)]);
    cartItems = cart.items;
    for (const item of items) {
      if (!managed(item)) continue;
      const variant = variants.get(String(item.id));
      const quantity = totalFor(items, item.id) + totalFor(cart.items, item.id);
      assertOrderable(variant, quantity);
      item.properties = properties(item.properties, isBackorder(variant, quantity));
    }
    return items;
  }
  async function prepareForm(formData) {
    const id = formData.get('id');
    const current = Object.fromEntries([...formData.entries()].filter(([key]) => /^properties\[.+\]$/.test(key)).map(([key,value]) => [key.slice(11,-1),value]));
    const item = {id,quantity:Number(formData.get('quantity') || 1),properties:current};
    if (!managed(item) && !scope.KickisKitStock?.isKit(item)) return;
    await prepareItems([item]);
    for (const key of Object.keys(current)) if (!(key in item.properties)) formData.delete(`properties[${key}]`);
    for (const [key,value] of Object.entries(item.properties)) formData.set(`properties[${key}]`,value);
  }
  async function reconcileCart() {
    let cart = await json(`${root()}cart.js`);
    if (scope.KickisKitStock) cart = await scope.KickisKitStock.reconcileCart(cart);
    if (!cart.items.some(managed)) return cart;
    const variants = await stock(cart.items);
    for (let pass = 0; pass < 50; pass++) {
      const totals = new Map(cart.items.filter(managed).map(item => [String(item.variant_id), totalFor(cart.items,item.variant_id)]));
      for (const [id,quantity] of totals) assertOrderable(variants.get(id),quantity);
      const item = cart.items.find(line => managed(line) && JSON.stringify(line.properties || {}) !== JSON.stringify(properties(line.properties,isBackorder(variants.get(String(line.variant_id)),totals.get(String(line.variant_id))))));
      if (!item) {cartItems = cart.items;return cart;}
      const next = properties(item.properties,isBackorder(variants.get(String(item.variant_id)),totals.get(String(item.variant_id))));
      if (!Object.keys(next).length) next._KickisBestallningsvara = '';
      cart = await json(`${root()}cart/change.js`,{id:item.key,quantity:item.quantity,properties:next});
    }
    throw Error(text.error);
  }
  function showCartError(error,scroll=false) {
    const hosts=[...document.querySelectorAll('.cart__ctas')];
    if(!hosts.length&&scroll&&document.querySelector('main'))hosts.push(document.querySelector('main'));
    for(const host of hosts){
      let notice=host.nextElementSibling;
      if(!notice?.matches('[data-backorder-error]')){notice=document.createElement('div');notice.dataset.backorderError='';notice.className='backorder-notice';host.after(notice);}
      if(scope.KickisKitErrors)scope.KickisKitErrors.render(notice,error,()=>refreshCart());else{notice.textContent=text.error;notice.setAttribute('role','alert');}
      if(scroll)notice.scrollIntoView({block:'center'});
    }
  }
  let checkoutPending = false;
  async function waitForCartUpdates() {
    for (let attempt=0;attempt<75;attempt++) {
      const pending = document.querySelector('.cart__items--disabled') || [...document.querySelectorAll('cart-items input[name="updates[]"],cart-drawer-items input[name="updates[]"]')].some(input=>input.value!==input.defaultValue);
      if (!pending) return;
      await new Promise(resolve=>setTimeout(resolve,200));
    }
    throw Error(text.error);
  }
  async function checkout() {
    if (checkoutPending) return;
    checkoutPending = true;
    document.body.classList.add('backorder-checking');
    try {
      await waitForCartUpdates();
      await reconcileCart();
      scope.location.assign(`${root()}checkout`);
    } catch (error) {
      showCartError(error,true);
    } finally {checkoutPending=false;document.body.classList.remove('backorder-checking');}
  }
  function refreshProduct() {
    for (const container of document.querySelectorAll('[data-backorder-product]')) {
      const host = container.closest('product-info') || container.parentElement;
      const id = host.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
      const variant = JSON.parse(container.querySelector('[data-backorder-variants]').textContent).find(v => String(v.id) === id);
      const quantity = requestedQuantity(id, Number(host.querySelector('[name="quantity"]')?.value || 1));
      const delayed = isBackorder(variant, quantity);
      host.classList.toggle('backorder-active',delayed);
      const notice = container.querySelector('[data-backorder-notice]');
      notice.hidden = !delayed;
      notice.textContent = `◷ ${text.message} ${text.combined} ${text.pickup}`;
    }
  }
  let cartRefreshRevision=0;
  async function refreshCart(event) {
    const revision=++cartRefreshRevision;
    try {
      let cart = Array.isArray(event?.cartData?.items) && Number.isFinite(event.cartData.item_count) ? event.cartData : await json(`${root()}cart.js`);
      if (scope.KickisKitStock) cart = await scope.KickisKitStock.reconcileCart(cart);
      if(revision!==cartRefreshRevision)return;
      for(const notice of document.querySelectorAll('[data-backorder-error]'))notice.remove();
      cartItems = cart.items;
      for (const notice of document.querySelectorAll('[data-kit-backorder-cart-notice]')) {
        const id=notice.dataset.variantId;
        const delayed=cart.items.some(i=>(!id||String(i.variant_id)===id)&&i.properties?._KickisKitBackorder==='1');
        notice.hidden=!delayed;notice.textContent=delayed?'◷ '+text.message+(id?'':' '+text.combined+' '+text.pickup):'';
      }
      refreshProduct();
      for (const builder of document.querySelectorAll('pattern-kits')) {
        if (builder.detail && (builder.currentYarns?.length || builder.currentNeedles?.length)) {builder.refreshYarnStockStates();builder.updateAddButtonState();}
      }
    } catch (error) {if(revision===cartRefreshRevision)showCartError(error);}
  }
  const api = {eligible,isBackorder,properties,prepareItems,prepareForm,reconcileCart,checkout,refreshProduct,refreshCart,requestedQuantity,pilot,text,totalFor};
  scope.KickisBackorder = api;
  if (typeof module !== 'undefined') module.exports = api;
  if (typeof document === 'undefined') return;
  document.addEventListener('input', event => {if(event.target.matches('[name="quantity"]'))refreshProduct();});
  document.addEventListener('change', event => {if(event.target.matches('[name="quantity"]'))refreshProduct();});
  document.addEventListener('DOMContentLoaded',()=>{refreshProduct();refreshCart();});
  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.variantChange, refreshProduct);
    subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCart);
  }
  const checkoutSelector = '[name="checkout"],a[href$="/checkout"]';
  document.addEventListener('click',event => {
    if (!event.target.closest(checkoutSelector)) return;
    const upsell = document.querySelector('[data-pre-checkout-upsell][data-enabled="true"]');
    if (upsell && scope.KickisUpsellEngine && scope.KickisUpsellLoader && !event.target.closest('a')) return;
    event.preventDefault();event.stopImmediatePropagation();checkout();
  },true);
  document.addEventListener('submit',event => {
    if (!event.submitter?.matches('[name="checkout"]')) return;
    const upsell = document.querySelector('[data-pre-checkout-upsell][data-enabled="true"]');
    if (upsell && scope.KickisUpsellEngine && scope.KickisUpsellLoader) return;
    event.preventDefault();event.stopImmediatePropagation();checkout();
  },true);
})(typeof window === 'undefined' ? globalThis : window);
